/**
 * PlaylistChartView Component
 * Radiomitor-style playlist chart with airplay log and dayparts views
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, Calendar, Clock, TrendingUp, TrendingDown, Minus,
  List, Grid3X3, ArrowUp, ArrowDown, X, ChevronLeft, ChevronRight,
  Download, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { getBackendUrl } from '../../utils/api';
import AudienceImpactBadge from '../AudienceImpactBadge';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = getBackendUrl();

// Fallback Greek day names — actual rendering uses translation hook
// via DAY_NAME_KEYS below. The pre-existing constant is kept so
// non-React utilities that consume DAY_NAMES still work.
const DAY_NAMES = ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ'];
const DAY_NAME_KEYS = ['i18nB3_day_mon', 'i18nB3_day_tue', 'i18nB3_day_wed', 'i18nB3_day_thu', 'i18nB3_day_fri', 'i18nB3_day_sat', 'i18nB3_day_sun'];

/**
 * QuarterPieChart - Shows a circle divided into 4 quarters
 * Only the quarter where the song played is filled with color
 * Colors: Blue (:00-:14), Green (:15-:29), Red (:30-:44), Yellow (:45-:59)
 */
const QuarterPieChart = ({ quarters = [false, false, false, false], plays = 0 }) => {
  // Quarter colors
  const colors = ['#3b82f6', '#22c55e', '#ef4444', '#eab308']; // blue, green, red, yellow
  
  // Check if quarters array exists and has data
  const hasQuarters = quarters && quarters.some(q => q);
  
  if (!hasQuarters) {
    // Fallback: show solid blue dot if no quarter data
    return (
      <div 
        className="w-5 h-5 mx-auto rounded-full bg-blue-500" 
        title={`${plays} play(s)`}
      />
    );
  }
  
  // Size of the pie chart
  const size = 22;
  const cx = 11, cy = 11, r = 9;
  
  // Calculate quarter paths
  const getQuarterPath = (idx) => {
    const startAngle = idx * 90 - 90; // Start from top (12 o'clock)
    const endAngle = (idx + 1) * 90 - 90;
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`;
  };
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`}
      className="mx-auto"
      title={`${plays} play(s)`}
    >
      {/* Draw circle outline */}
      <circle 
        cx={cx} 
        cy={cy} 
        r={r} 
        fill="none" 
        stroke="#4b5563" 
        strokeWidth="1.5"
      />
      
      {/* Draw quarter divider lines */}
      <line x1={cx} y1={cy-r} x2={cx} y2={cy+r} stroke="#4b5563" strokeWidth="0.8" />
      <line x1={cx-r} y1={cy} x2={cx+r} y2={cy} stroke="#4b5563" strokeWidth="0.8" />
      
      {/* Fill only the quarters that have plays */}
      {quarters.map((active, idx) => {
        if (!active) return null;
        return (
          <path 
            key={idx} 
            d={getQuarterPath(idx)} 
            fill={colors[idx]}
            stroke={colors[idx]}
            strokeWidth="0.5"
          />
        );
      })}
    </svg>
  );
};

const PlaylistChartView = ({ stationId, stationName, onClose, embedded = false, initialPeriod = null }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('chart'); // chart, log, dayparts
  const [chartData, setChartData] = useState(null);
  const [logData, setLogData] = useState(null);
  const [daypartsData, setDaypartsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  // Map SongsAnalyzer periods to week-based periods for dayparts
  // Dayparts is designed for full weeks only
  const mapToWeekPeriod = (p) => {
    const weekPeriods = new Set(['week', 'weeks_2', 'weeks_3']);
    if (weekPeriods.has(p)) return p;
    // Map non-week periods to "week"
    return 'week';
  };
  const [period, setPeriod] = useState(mapToWeekPeriod(initialPeriod || 'week'));
  const [hourStart, setHourStart] = useState(6);
  const [hourEnd, setHourEnd] = useState(21);
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  
  // ──────────────────────────────────────────────────────────────────
  // Stale-state fix (2026-05-02): when the parent SongsAnalyzer changes
  // its period dropdown, the new value arrives via `initialPeriod` prop.
  // useState only reads its initialValue once at mount, so without this
  // sync the component shows stale chart data until manual refresh.
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (initialPeriod) {
      setPeriod(mapToWeekPeriod(initialPeriod));
    }
    // The eslint react-hooks plugin isn't installed in this project, so
    // we omit the previous rule-specific disable comment that triggered
    // a "rule definition not found" warning in the webpack overlay.
  }, [initialPeriod]);
  
  // Selected song for log/dayparts view
  const [selectedSong, setSelectedSong] = useState(null);
  const [selectedSongIndex, setSelectedSongIndex] = useState(-1);
  
  // Audience Impact data
  const [impactLookup, setImpactLookup] = useState({});
  
  // Fetch audience impact for this station
  useEffect(() => {
    const fetchImpact = async () => {
      try {
        const days = period === 'week' ? 7 : period === 'weeks_3' ? 21 : 14;
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/audience-impact/station/${stationId}?days=${days}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          // Build lookup with both normalized_key AND display_key
          const songs = data.songs || {};
          const enhanced = {};
          Object.entries(songs).forEach(([k, v]) => {
            enhanced[k] = v;
          });
          setImpactLookup(enhanced);
        }
      } catch (e) { /* silent */ }
    };
    if (stationId) fetchImpact();
  }, [stationId, period]);
  
  // Fetch playlist chart
  const fetchChart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const daysParam = selectedDays.join(',');
      const response = await fetch(
        `${BACKEND_URL}/api/playlist-chart/station/${stationId}/chart?period=${period}&hour_start=${hourStart}&hour_end=${hourEnd}&days=${daysParam}&limit=200`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setChartData(data);
    } catch (err) {
      console.error('Chart fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId, period, hourStart, hourEnd, selectedDays]);
  
  // Fetch airplay log for a song - use canonical_song_id for complete grouping
  const fetchAirplayLog = useCallback(async (song) => {
    setLoading(true);
    setError(null);
    try {
      // v2026-04-11: Prefer multi-key params (groups ALL fuzzy-merged variants).
      // Fallback chain: merged ids → single canonical_song_id → normalized_key → artist+title.
      const mergedCsids = Array.isArray(song.merged_canonical_ids) ? song.merged_canonical_ids.filter(Boolean) : [];
      const mergedNks = Array.isArray(song.merged_normalized_keys) ? song.merged_normalized_keys.filter(Boolean) : [];
      let params;
      if (mergedCsids.length > 1 || mergedNks.length > 1) {
        const parts = [];
        if (mergedCsids.length) parts.push(`canonical_song_ids=${encodeURIComponent(mergedCsids.join(','))}`);
        if (mergedNks.length) parts.push(`normalized_keys=${encodeURIComponent(mergedNks.join(','))}`);
        parts.push(`period=${period}`);
        params = parts.join('&');
      } else if (song.canonical_song_id) {
        params = `canonical_song_id=${encodeURIComponent(song.canonical_song_id)}&period=${period}`;
      } else if (song.normalized_key) {
        params = `normalized_key=${encodeURIComponent(song.normalized_key)}&period=${period}`;
      } else {
        params = `artist=${encodeURIComponent(song.artist)}&title=${encodeURIComponent(song.title)}&period=${period}`;
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/playlist-chart/station/${stationId}/airplay-log?${params}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setLogData(data);
      setActiveTab('log');
    } catch (err) {
      console.error('Airplay log fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId, period]);
  
  // Fetch dayparts for a song - use canonical_song_id for complete grouping
  const fetchDayparts = useCallback(async (song) => {
    setLoading(true);
    setError(null);
    try {
      // v2026-04-11: Prefer multi-key params (groups ALL fuzzy-merged variants).
      const mergedCsids = Array.isArray(song.merged_canonical_ids) ? song.merged_canonical_ids.filter(Boolean) : [];
      const mergedNks = Array.isArray(song.merged_normalized_keys) ? song.merged_normalized_keys.filter(Boolean) : [];
      let params;
      if (mergedCsids.length > 1 || mergedNks.length > 1) {
        const parts = [];
        if (mergedCsids.length) parts.push(`canonical_song_ids=${encodeURIComponent(mergedCsids.join(','))}`);
        if (mergedNks.length) parts.push(`normalized_keys=${encodeURIComponent(mergedNks.join(','))}`);
        parts.push(`period=${period}`);
        params = parts.join('&');
      } else if (song.canonical_song_id) {
        params = `canonical_song_id=${encodeURIComponent(song.canonical_song_id)}&period=${period}`;
      } else if (song.normalized_key) {
        params = `normalized_key=${encodeURIComponent(song.normalized_key)}&period=${period}`;
      } else {
        params = `artist=${encodeURIComponent(song.artist)}&title=${encodeURIComponent(song.title)}&period=${period}`;
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/playlist-chart/station/${stationId}/dayparts?${params}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDaypartsData(data);
      setActiveTab('dayparts');
    } catch (err) {
      console.error('Dayparts fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId, period]);
  
  // Load chart on mount and filter changes
  useEffect(() => {
    if (activeTab === 'chart') {
      fetchChart();
    }
  }, [fetchChart, activeTab]);
  
  // Toggle day selection
  const toggleDay = (dayIdx) => {
    if (selectedDays.includes(dayIdx)) {
      setSelectedDays(selectedDays.filter(d => d !== dayIdx));
    } else {
      setSelectedDays([...selectedDays, dayIdx].sort());
    }
  };
  
  // Handle song click - show options and set index
  const handleSongClick = (song) => {
    setSelectedSong(song);
    // Find and set the index
    if (chartData?.chart) {
      const idx = chartData.chart.findIndex(s => s.normalized_key === song.normalized_key);
      setSelectedSongIndex(idx);
    }
  };
  
  // Handle song selection for log/dayparts with index tracking
  const handleFetchAirplayLog = (song, index) => {
    setSelectedSong(song);
    setSelectedSongIndex(index);
    fetchAirplayLog(song);
  };
  
  const handleFetchDayparts = (song, index) => {
    setSelectedSong(song);
    setSelectedSongIndex(index);
    fetchDayparts(song);
  };
  
  // Navigation functions for prev/next song
  const goToPrevSong = () => {
    if (!chartData?.chart || selectedSongIndex <= 0) return;
    const prevSong = chartData.chart[selectedSongIndex - 1];
    setSelectedSong(prevSong);
    setSelectedSongIndex(selectedSongIndex - 1);
    if (activeTab === 'log') {
      fetchAirplayLog(prevSong);
    } else if (activeTab === 'dayparts') {
      fetchDayparts(prevSong);
    }
  };
  
  const goToNextSong = () => {
    if (!chartData?.chart || selectedSongIndex >= chartData.chart.length - 1) return;
    const nextSong = chartData.chart[selectedSongIndex + 1];
    setSelectedSong(nextSong);
    setSelectedSongIndex(selectedSongIndex + 1);
    if (activeTab === 'log') {
      fetchAirplayLog(nextSong);
    } else if (activeTab === 'dayparts') {
      fetchDayparts(nextSong);
    }
  };
  
  // Render trend indicator
  // 2026-05-15: User requested removal of "NEW" label from chart UI because
  // duplicate canonicals were producing false NEW indicators for songs that
  // were already in rotation for months. Fall through to neutral dash.
  const renderTrend = (trend, trendPct) => {
    if (trend === 'NEW') {
      return <span className="text-gray-400 flex items-center gap-1"><Minus size={14} />-</span>;
    }
    if (trendPct > 0) {
      return <span className="text-green-500 flex items-center gap-1"><TrendingUp size={14} />{trend}</span>;
    }
    if (trendPct < 0) {
      return <span className="text-red-500 flex items-center gap-1"><TrendingDown size={14} />{trend}</span>;
    }
    return <span className="text-gray-400 flex items-center gap-1"><Minus size={14} />-</span>;
  };
  
  // Render position change
  // 2026-05-15: Same fix — show "-" instead of "NEW" when previous position
  // is missing (often caused by canonical-ID drift, not genuine new entries).
  const renderPositionChange = (pos, prevPos) => {
    if (!prevPos) return <span className="text-gray-400 text-xs">-</span>;
    if (pos < prevPos) {
      return <span className="text-green-500 flex items-center"><ArrowUp size={12} />{prevPos - pos}</span>;
    }
    if (pos > prevPos) {
      return <span className="text-red-500 flex items-center"><ArrowDown size={12} />{pos - prevPos}</span>;
    }
    return <span className="text-gray-400">-</span>;
  };
  
  // Wrapper classes based on embedded mode
  const wrapperClasses = embedded
    ? "w-full" // Inline mode - no fixed positioning
    : "fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"; // Modal mode
  
  const containerClasses = embedded
    ? "bg-gray-900 rounded-lg shadow-xl w-full overflow-hidden flex flex-col border border-gray-700"
    : "bg-gray-900 rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-700";
  
  return (
    <div className={wrapperClasses}>
      <div className={containerClasses}>
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-700 bg-gray-800">
          <div className="flex items-center gap-3">
            <BarChart3 className="text-orange-500" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-gray-100">{stationName} - Playlist Chart</h2>
              <p className="text-xs text-gray-400">
                {chartData?.date_range?.start && `${new Date(chartData.date_range.start).toLocaleDateString('el-GR')} - ${new Date(chartData.date_range.end).toLocaleDateString('el-GR')}`}
              </p>
            </div>
          </div>
          {!embedded && (
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-gray-200">
              <X size={20} />
            </Button>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 p-2 border-b border-gray-700 bg-gray-800">
          <Button
            variant={activeTab === 'chart' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('chart')}
          >
            <BarChart3 size={16} className="mr-2" />
            Playlist Chart
          </Button>
          {selectedSong && activeTab !== 'chart' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveTab('chart')}
              className="text-orange-400 border-orange-400 hover:bg-orange-400/20"
            >
              <ChevronLeft size={16} className="mr-1" />
              Πίσω
            </Button>
          )}
{/* Song info moved to detail views */}
        </div>
        
        {/* Filters (for chart view) */}
        {activeTab === 'chart' && (
          <div className="flex flex-wrap items-center gap-3 p-2 border-b border-gray-700 bg-gray-800">
            {/* Period */}
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-gray-500" />
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="text-xs border border-gray-600 rounded px-2 py-1 bg-gray-700 text-gray-200"
              >
                <option value="week">Προηγ. Εβδομάδα</option>
                <option value="weeks_2">2 Εβδομάδες</option>
                <option value="weeks_3">3 Εβδομάδες</option>
              </select>
            </div>
            
            {/* Hours */}
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-gray-500" />
              <select
                value={hourStart}
                onChange={(e) => setHourStart(parseInt(e.target.value))}
                className="text-xs border border-gray-600 rounded px-2 py-1 bg-gray-700 text-gray-200 w-16"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
              <span className="text-gray-500">-</span>
              <select
                value={hourEnd}
                onChange={(e) => setHourEnd(parseInt(e.target.value))}
                className="text-xs border border-gray-600 rounded px-2 py-1 bg-gray-700 text-gray-200 w-16"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            
            {/* Days */}
            <div className="flex items-center gap-1">
              {DAY_NAMES.map((day, idx) => (
                <button
                  key={idx}
                  onClick={() => toggleDay(idx)}
                  className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                    selectedDays.includes(idx)
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-600 text-gray-400'
                  }`}
                >
                  {t(DAY_NAME_KEYS[idx]) || day}
                </button>
              ))}
            </div>
            
            {/* Apply button */}
            <Button size="sm" onClick={fetchChart} className="bg-orange-600 hover:bg-orange-700 text-white text-xs px-3 py-1 h-auto">
              <Filter size={12} className="mr-1" />
              Εφαρμογή
            </Button>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-2 bg-gray-900">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          )}
          
          {error && (
            <div className="bg-red-900/20 text-red-400 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {/* Chart View */}
          {!loading && activeTab === 'chart' && chartData && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-orange-600 text-white">
                    <th className="px-2 py-1 text-left">Pos</th>
                    <th className="px-2 py-1 text-left">±</th>
                    <th className="px-2 py-1 text-left">Καλλιτέχνης</th>
                    <th className="px-2 py-1 text-left">Τίτλος</th>
                    <th className="px-2 py-1 text-center">
                      Plays
                      <div className="text-[10px] font-normal opacity-80">
                        {chartData.date_range?.current_label || ''}
                      </div>
                    </th>
                    <th className="px-2 py-1 text-center">
                      Prev
                      <div className="text-[10px] font-normal opacity-80">
                        {chartData.date_range?.prev_label || ''}
                      </div>
                    </th>
                    <th className="px-2 py-1 text-center">Trend</th>
                    <th className="px-2 py-1 text-center" title="Retention: Ποσοστό ακροατών που έμειναν">Ret%</th>
                    <th className="px-2 py-1 text-center" title="Early Drop: Ποσοστό που έφυγε στα πρώτα 30&quot;">Early</th>
                    <th className="px-2 py-1 text-center" title="Listener Stability Index">LSI</th>
                    <th className="px-2 py-1 text-center" title="Composite Score">Score</th>
                    <th className="px-2 py-1 text-center">
                      <div>A.Impact</div>
                      <div className="text-[10px] font-normal opacity-80">Audience</div>
                    </th>
                    <th className="px-2 py-1 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.chart.map((song, idx) => (
                    <tr
                      key={song.normalized_key}
                      className={`border-b border-gray-700 hover:bg-blue-900/30 cursor-pointer ${
                        selectedSong?.normalized_key === song.normalized_key ? 'bg-blue-900/40' : ''
                      } ${idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}`}
                      onClick={() => handleSongClick(song)}
                    >
                      <td className="px-2 py-1 font-bold text-orange-400">{song.position}</td>
                      <td className="px-2 py-1">{renderPositionChange(song.position, song.prev_position)}</td>
                      <td className="px-2 py-1 font-medium text-gray-200 max-w-[160px] truncate" title={song.artist}>{song.artist}</td>
                      <td className="px-2 py-1 text-blue-400 max-w-[180px] truncate" title={song.title}>{song.title}</td>
                      <td className="px-2 py-1 text-center font-bold text-blue-400">{song.plays}</td>
                      <td className="px-2 py-1 text-center text-gray-500">{song.prev_plays || '-'}</td>
                      <td className="px-2 py-1 text-center">{renderTrend(song.trend, song.trend_pct)}</td>
                      <td className="px-2 py-1 text-center">
                        <span className={
                          song.retention_rate >= 90 ? 'text-green-400' :
                          song.retention_rate >= 75 ? 'text-yellow-400' :
                          song.retention_rate >= 60 ? 'text-orange-400' :
                          song.retention_rate > 0 ? 'text-red-400' : 'text-gray-500'
                        }>
                          {song.retention_rate > 0 ? `${Math.min(song.retention_rate, 100)}%` : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={
                          song.early_drop_rate != null
                            ? song.early_drop_rate <= 5 ? 'text-green-400' : song.early_drop_rate <= 10 ? 'text-yellow-400' : 'text-red-400'
                            : 'text-gray-500'
                        }>
                          {song.early_drop_rate != null ? `${song.early_drop_rate.toFixed(0)}%` : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={
                          song.lsi != null
                            ? song.lsi >= 1.0 ? 'text-blue-400' : song.lsi >= 0.9 ? 'text-green-400' : song.lsi >= 0.8 ? 'text-yellow-400' : 'text-orange-400'
                            : 'text-gray-500'
                        }>
                          {song.lsi != null ? song.lsi.toFixed(2) : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center font-medium text-blue-400">
                        {song.composite_score || '-'}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {(() => {
                          const imp = impactLookup[song.normalized_key];
                          return imp ? (
                            <AudienceImpactBadge value={imp.audience_impact} level={imp.level} compact />
                          ) : <span className="text-white/20">-</span>;
                        })()}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleFetchAirplayLog(song, idx); }}
                            className="text-xs text-blue-400 hover:underline"
                          >
                            log
                          </button>
                          <span className="text-gray-600">|</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleFetchDayparts(song, idx); }}
                            className="text-xs text-blue-400 hover:underline"
                          >
                            dayparts
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {chartData.chart.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Δεν βρέθηκαν τραγούδια για τα επιλεγμένα κριτήρια
                </div>
              )}
            </div>
          )}
          
          {/* Airplay Log View */}
          {!loading && activeTab === 'log' && logData && (
            <div>
              {/* Navigation bar - fixed width buttons on sides, title centered */}
              <div className="grid grid-cols-[48px_1fr_48px] items-center mb-2">
                <button
                  onClick={goToPrevSong}
                  disabled={selectedSongIndex <= 0}
                  className={`p-2 rounded-lg justify-self-start ${selectedSongIndex <= 0 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-700 bg-gray-800'}`}
                  title={t('i18nB3_prev_song')}
                  data-testid="airplay-prev-btn"
                >
                  <ChevronLeft size={24} />
                </button>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-200">{logData.artist} - {logData.title}</h3>
                  <p className="text-xs text-gray-400">
                    {chartData?.chart && <span>({selectedSongIndex + 1} / {chartData.chart.length})</span>}
                  </p>
                </div>
                <button
                  onClick={goToNextSong}
                  disabled={!chartData?.chart || selectedSongIndex >= chartData.chart.length - 1}
                  className={`p-2 rounded-lg justify-self-end ${!chartData?.chart || selectedSongIndex >= chartData.chart.length - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-700 bg-gray-800'}`}
                  title={t('i18nB3_next_song')}
                  data-testid="airplay-next-btn"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
              
              {/* Info bar */}
              <div className="mb-2 p-2 bg-blue-900/30 rounded-lg flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  Airplay {logData.station_name} | Total plays: <span className="font-bold text-blue-400">{logData.total_plays}</span>
                </p>
                <button
                  onClick={() => fetchDayparts(logData.artist, logData.title)}
                  className="text-xs bg-gray-700 hover:bg-gray-600 text-blue-400 px-3 py-1 rounded"
                >
                  <Grid3X3 size={12} className="inline mr-1" />
                  Dayparts
                </button>
              </div>
              
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-700">
                    <th className="px-2 py-1 text-left text-gray-300">Station</th>
                    <th className="px-2 py-1 text-left text-gray-300">Artist - Title</th>
                    <th className="px-2 py-1 text-right text-gray-300">Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logData.plays.map((play, idx) => (
                    <tr key={idx} className={`border-b border-gray-700 hover:bg-gray-700 ${idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}`}>
                      <td className="px-2 py-1 text-gray-300">{play.station}</td>
                      <td className="px-2 py-1 text-gray-200">{play.artist} - {play.title}</td>
                      <td className="px-2 py-1 text-right text-gray-400">{play.datetime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Dayparts View */}
          {!loading && activeTab === 'dayparts' && daypartsData && (
            <div>
              {/* Navigation bar - fixed width buttons on sides, title centered */}
              <div className="grid grid-cols-[48px_1fr_48px] items-center mb-2">
                <button
                  onClick={goToPrevSong}
                  disabled={selectedSongIndex <= 0}
                  className={`p-2 rounded-lg justify-self-start ${selectedSongIndex <= 0 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-700 bg-gray-800'}`}
                  title={t('i18nB3_prev_song')}
                  data-testid="dayparts-prev-btn"
                >
                  <ChevronLeft size={24} />
                </button>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-200">{daypartsData.artist} - {daypartsData.title}</h3>
                  <p className="text-xs text-gray-400">
                    {chartData?.chart && <span>({selectedSongIndex + 1} / {chartData.chart.length})</span>}
                  </p>
                </div>
                <button
                  onClick={goToNextSong}
                  disabled={!chartData?.chart || selectedSongIndex >= chartData.chart.length - 1}
                  className={`p-2 rounded-lg justify-self-end ${!chartData?.chart || selectedSongIndex >= chartData.chart.length - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-700 bg-gray-800'}`}
                  title={t('i18nB3_next_song')}
                  data-testid="dayparts-next-btn"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
              
              {/* Info bar */}
              <div className="mb-2 p-2 bg-blue-900/30 rounded-lg flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {daypartsData.date_range_label || `${daypartsData.date_range.start} - ${daypartsData.date_range.end}`} | {daypartsData.station_name} | {daypartsData.total_plays} plays
                </p>
                <button
                  onClick={() => fetchAirplayLog(daypartsData.artist, daypartsData.title)}
                  className="text-xs bg-gray-700 hover:bg-gray-600 text-blue-400 px-3 py-1 rounded"
                >
                  <List size={12} className="inline mr-1" />
                  Airplay Log
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="text-xs" style={{ minWidth: '900px' }}>
                  <thead>
                    <tr className="bg-gray-700">
                      <th className="px-1 py-1 text-left sticky left-0 bg-gray-700 text-gray-300 w-20">Date</th>
                      <th className="px-1 py-1 text-center bg-gray-700 text-orange-400 w-10">Plays</th>
                      {Array.from({ length: 24 }, (_, h) => (
                        <th key={h} className="py-1 text-center text-gray-400" style={{ width: '26px', fontSize: '10px' }}>{String(h).padStart(2, '0')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {daypartsData.dayparts.slice(0, 14).map((day, idx) => (
                      <tr key={day.date} className={`border-b border-gray-700 ${idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}`}>
                        <td className="px-1 py-1 font-medium sticky left-0 text-gray-300 whitespace-nowrap" style={{ background: idx % 2 === 0 ? '#1f2937' : '#1e293b' }}>{day.label}</td>
                        <td className="px-1 py-1 text-center font-bold text-orange-400">{day.total_plays}</td>
                        {day.hours.map((hour) => (
                          <td key={hour.hour} className="py-1 text-center" style={{ padding: '2px' }}>
                            {hour.has_play ? (
                              <QuarterPieChart quarters={hour.quarters} plays={hour.plays} />
                            ) : null}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legend for quarters */}
              <div className="mt-3 flex items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                  <span className="text-gray-400">:00-:14</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                  <span className="text-gray-400">:15-:29</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                  <span className="text-gray-400">:30-:44</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-yellow-500 rounded-sm"></div>
                  <span className="text-gray-400">:45-:59</span>
                </div>
              </div>
              
              <div className="mt-2 text-xs text-gray-500">
                Total plays: <span className="font-bold text-blue-400">{daypartsData.total_plays}</span> over <span className="font-bold">{daypartsData.total_days}</span> days
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaylistChartView;
